<template>
  <div class="container mt-4">
    <TopNav>
      <div class="d-flex" v-if="email">
        <NavItem
          icon="far fa-eye"
          :text="$t('MARKETING.LINKS.PREVIEW')"
          :loading="isPreviewing"
          @click.native="preview"
        />
        <NavItem
          v-if="!isSent"
          icon="far fa-save"
          :text="$t('MARKETING.LINKS.SAVE')"
          :pulse="isDirty"
          :loading="isLoading"
          @click.native="save"
        />
        <NavItem
          v-if="!isSent && isMailServerConnected"
          icon="far fa-paper-plane"
          :text="$t('MARKETING.LINKS.SEND_TEST_EMAIL')"
          :loading="isSending"
          :disabled="isSending"
          @click.native="showTestSendModal"
        />
        <NavItem
          v-if="!isSent && isMailServerConnected"
          icon="far fa-envelope"
          :text="$t('MARKETING.LINKS.SEND')"
          :loading="isSending"
          :disabled="isSending"
          @click.native="showSendModal"
        />
      </div>
    </TopNav>
    <div v-if="email">
      <div v-if="!isSent" class="mt-4">
        <div class="row">
          <div class="col-12 col-md-4">
            <section class="filter-section">
              <div
                class="d-flex flex-row justify-content-between align-items-center mb-2"
              >
                <div>
                  <span
                    class="c-dark-gray mr-2"
                    v-tooltip="{
                      content: $t('MARKETING.TOOLTIPS.IS_MARKETING_EMAIL'),
                      delay: 100,
                      placement: 'top',
                      container: 'body',
                      trigger: 'click hover',
                    }"
                  >
                    <i class="fal fa-info-circle"></i>
                  </span>
                  <span class="c-charcoal">
                    {{ $t('MARKETING.LABELS.MARKETING_EMAIL') }}
                  </span>
                </div>
                <HMSwitch
                  v-model="email.is_marketing_email"
                  @input="fetchAudience"
                />
              </div>
              <hr />
              <div class="head">
                <span class="c-charcoal">{{
                  $t('MARKETING.LABELS.RECIPIENTS')
                }}</span>
                <span class="c-charcoal"
                  >{{ selectedAudience }}/{{ totalAudience }}</span
                >
              </div>
              <div>
                <HMRadio
                  class="mt-3"
                  group="email-everyone"
                  name="everyone"
                  v-model="recipients"
                  @input="fetchAudience"
                >
                  {{ $t('MARKETING.LABELS.EVERYONE') }}
                </HMRadio>
                <HMRadio
                  class="mt-2"
                  group="email-everyone"
                  name="customers_only"
                  v-model="recipients"
                  @input="fetchAudience"
                >
                  {{ $t('MARKETING.LABELS.CUSTOMERS_ONLY') }}
                </HMRadio>
                <HMRadio
                  class="mt-2"
                  group="email-everyone"
                  name="www_customers"
                  v-model="recipients"
                  @input="fetchAudience"
                >
                  {{ $t('MARKETING.LABELS.WWW_CUSTOMERS') }}
                </HMRadio>
              </div>
            </section>
            <section v-if="showPurchasedFilters">
              <section class="filter-section">
                <div class="head">
                  <span class="c-charcoal">{{
                    $t('MARKETING.LABELS.HAS_BOUGHT')
                  }}</span>
                  <span
                    class="c-dark-gray"
                    v-tooltip="{
                      content: $t('MARKETING.TOOLTIPS.HAS_BOUGHT'),
                      delay: 100,
                      placement: 'top',
                      container: 'body',
                      trigger: 'click hover',
                    }"
                  >
                    <i class="fal fa-info-circle"></i>
                  </span>
                </div>
                <div>
                  <HMRadio
                    class="mt-3"
                    group="purchased"
                    name="all"
                    v-model="email.purchased_operator"
                    @input="fetchAudience"
                  >
                    {{ $t('MARKETING.LABELS.ALL_OF_THE_SELECTED_PRODUCTS') }}
                  </HMRadio>
                  <HMRadio
                    class="mt-2 mb-3"
                    group="purchased"
                    name="any"
                    v-model="email.purchased_operator"
                    @input="fetchAudience"
                  >
                    {{ $t('MARKETING.LABELS.ANY_OF_THE_SELECTED_PRODUCTS') }}
                  </HMRadio>
                  <v-select
                    v-if="products"
                    :placeholder="$t('PRODUCTS.COUPON_FORM.SELECT_PRODUCT')"
                    :options="products"
                    :multiple="true"
                    label="product_name"
                    code="id"
                    v-model="email.purchased_products"
                    @input="fetchAudience"
                  />
                </div>
              </section>
              <section class="filter-section">
                <div class="head">
                  <span class="c-charcoal">{{
                    $t('MARKETING.LABELS.HAS_NOT_BOUGHT')
                  }}</span>
                  <span
                    class="c-dark-gray"
                    v-tooltip="{
                      content: $t('MARKETING.TOOLTIPS.HAS_NOT_BOUGHT'),
                      delay: 100,
                      placement: 'top',
                      container: 'body',
                      trigger: 'click hover',
                    }"
                  >
                    <i class="fal fa-info-circle"></i>
                  </span>
                </div>
                <div>
                  <HMRadio
                    class="mt-3"
                    group="unpurchased"
                    name="all"
                    v-model="email.unpurchased_operator"
                    @input="fetchAudience"
                  >
                    {{ $t('MARKETING.LABELS.ALL_OF_THE_SELECTED_PRODUCTS') }}
                  </HMRadio>
                  <HMRadio
                    class="mt-2 mb-3"
                    group="unpurchased"
                    name="any"
                    v-model="email.unpurchased_operator"
                    @input="fetchAudience"
                  >
                    {{ $t('MARKETING.LABELS.ANY_OF_THE_SELECTED_PRODUCTS') }}
                  </HMRadio>
                  <v-select
                    v-if="products"
                    :placeholder="$t('PRODUCTS.COUPON_FORM.SELECT_PRODUCT')"
                    :options="products"
                    :multiple="true"
                    label="product_name"
                    code="id"
                    v-model="email.unpurchased_products"
                    @input="fetchAudience"
                  />
                </div>
              </section>
              <section class="filter-section">
                <div class="head">
                  <span class="c-charcoal">{{
                    $t('MARKETING.LABELS.PURCHASE_WAS_MADE')
                  }}</span>
                  <span
                    class="c-dark-gray"
                    v-tooltip="{
                      content: $t('MARKETING.TOOLTIPS.PURCHASE_DATE'),
                      delay: 100,
                      placement: 'top',
                      container: 'body',
                      trigger: 'click hover',
                    }"
                  >
                    <i class="fal fa-info-circle"></i>
                  </span>
                </div>
                <div>
                  <HMRadio
                    class="mt-3"
                    group="datePurchased"
                    :name="true"
                    v-model="dateBefore"
                    @input="purchasedAfter = null"
                  >
                    {{ $t('MARKETING.LABELS.PURCHASED_BEFORE') }}
                  </HMRadio>
                  <HMRadio
                    class="mt-2 mb-3"
                    group="datePurchased"
                    :name="false"
                    v-model="dateBefore"
                    @input="purchasedBefore = null"
                  >
                    {{ $t('MARKETING.LABELS.PURCHASED_AFTER') }}
                  </HMRadio>
                  <HMDateSelect
                    v-if="dateBefore"
                    v-model="purchasedBefore"
                    :key="dateBefore"
                    @input="fetchAudience"
                  />
                  <HMDateSelect
                    v-if="!dateBefore"
                    v-model="purchasedAfter"
                    :key="dateBefore"
                    @input="fetchAudience"
                  />
                </div>
              </section>
            </section>
            <section v-if="showWWWCustomersFilter">
              <section class="filter-section">
                <div class="head">
                  <span class="c-charcoal">{{
                    $t('MARKETING.LABELS.REGISTRATION_WAS_MADE')
                  }}</span>
                  <span
                    class="c-dark-gray"
                    v-tooltip="{
                      content: $t('MARKETING.TOOLTIPS.REGISTRATION_DATE'),
                      delay: 100,
                      placement: 'top',
                      container: 'body',
                      trigger: 'click hover',
                    }"
                  >
                    <i class="fal fa-info-circle"></i>
                  </span>
                </div>
                <div>
                  <HMRadio
                    class="mt-3"
                    group="datePurchased"
                    :name="true"
                    v-model="dateBefore"
                    @input="purchasedAfter = null"
                  >
                    {{ $t('MARKETING.LABELS.PURCHASED_BEFORE') }}
                  </HMRadio>
                  <HMRadio
                    class="mt-2 mb-3"
                    group="datePurchased"
                    :name="false"
                    v-model="dateBefore"
                    @input="purchasedBefore = null"
                  >
                    {{ $t('MARKETING.LABELS.PURCHASED_AFTER') }}
                  </HMRadio>
                  <HMDateSelect
                    v-if="dateBefore"
                    v-model="purchasedBefore"
                    :key="dateBefore"
                    @input="fetchAudience"
                  />
                  <HMDateSelect
                    v-if="!dateBefore"
                    v-model="purchasedAfter"
                    :key="dateBefore"
                    @input="fetchAudience"
                  />
                </div>
              </section>
            </section>
          </div>
          <div class="col-12 col-md-8">
            <div class="form-group d-flex flex-row align-items-center mb-3">
              <label class="c-charcoal m-0 mr-4">
                {{ $t('MARKETING.LABELS.SHOW_LOGO_IN_EMAIL_HEADER') }}
              </label>
              <HMSwitch v-model="email.include_logo" />
            </div>
            <div class="form-group d-flex flex-row align-items-center mb-0">
              <HMTextInput
                class="w-100"
                v-model="email.subject"
                placeholder="Email subject"
              />
            </div>
            <div class="form-group d-flex flex-row align-items-center mb-2">
              <HMVueEditor
                id="hm-vue-email-editor"
                class="w-100"
                v-model="email.content"
                placeholder="Your email content"
              />
            </div>
            <div class="mt-4">
              <label>{{ $t('MARKETING.TEXT.WHEN_SCHEDULE_EMAIL') }}</label>
              <div class="d-flex align-items-center w-100 mt-2">
                <span class="calendar-icon mr-2 flex-shrink-0">
                  <i class="far fa-calendar"></i>
                </span>
                <span class="flex-grow-1 c-charcoal">
                  {{ $t('MARKETING.LABELS.SCHEDULED') }}:
                  <b v-if="!scheduledDate">
                    {{ $t('MARKETING.LABELS.IMMEDIATELY') }}
                  </b>
                  <b v-else>
                    {{ $formatDate(scheduledDate.startDate, 'date-time') }}
                  </b>
                </span>
                <HMDateSelect
                  v-model="scheduledDate"
                  :showClear="true"
                  :showTime="true"
                  :minDate="minimumDate"
                  :openPicker="false"
                >
                  <a class="c-brand-color" role="button">
                    <span v-if="scheduledDate">
                      {{ $t('MARKETING.LABELS.EDIT_DELIVERY_DATE') }}
                    </span>
                    <span v-else>
                      {{ $t('MARKETING.LABELS.SCHEDULE_FOR_LATER') }}
                    </span>
                  </a>
                </HMDateSelect>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <h2>
          {{
            $t('MARKETING.TITLES.RECIPIENTS_OF', {
              email_subject: email.subject,
            })
          }}
        </h2>
        <div
          class="d-flex flex-column flex-md-row justify-content-start align-items-center mt-4"
        >
          <div class="flex-grow-1 mr-3">
            <HMSearch v-model="search" />
          </div>
          <div
            class="d-flex flew-row justify-content-between align-items-center"
          >
            <HMCheckBox class="mx-2" v-model="opened">
              {{ $t('MARKETING.TABLE.OPENED') }}
            </HMCheckBox>
            <HMCheckBox class="mx-2" v-model="clicked">
              {{ $t('MARKETING.TABLE.CLICKED') }}
            </HMCheckBox>
            <HMCheckBox v-model="unsubscribed">
              {{ $t('MARKETING.TABLE.UNSUBSCRIBED') }}
            </HMCheckBox>
          </div>
        </div>
        <HMPaginatedTable
          name="recipients-list-table"
          api="marketing/getEmailRecipients"
          :fields="recipientsTableFields"
          :query="query"
          class="mt-4"
          ref="recipients-list-table"
          :pageInQuery="true"
          :perPageFixed="10"
          :disableHover="true"
        >
          <template v-slot:email="{ data }">
            <router-link :to="`/customers/${data.student.id}`" class="d-block">
              {{ data.email }}
            </router-link>
          </template>
          <template v-slot:sent="{ data }">
            <HMCheck
              v-tooltip="{
                content: data.sent
                  ? $formatDate(data.sent_at, 'date-time')
                  : data.send_error,
                delay: 100,
                placement: 'top',
                container: 'body',
              }"
              v-model="data.sent"
            />
          </template>
          <template v-slot:opened="{ data }">
            <HMCheck
              v-tooltip="{
                content: data.opened_at
                  ? $formatDate(data.opened_at, 'date-time')
                  : '',
                delay: 100,
                placement: 'top',
                container: 'body',
              }"
              v-model="data.opened"
            />
          </template>
          <template v-slot:clicked="{ data }">
            <HMCheck
              v-tooltip="{
                content: data.clicked_at
                  ? $formatDate(data.clicked_at, 'date-time')
                  : '',
                delay: 100,
                placement: 'top',
                container: 'body',
              }"
              v-model="data.clicked"
            />
          </template>
          <template v-slot:unsubscribed="{ data }">
            <HMCheck
              v-tooltip="{
                content: data.unsubscribed_at
                  ? $formatDate(data.unsubscribed_at, 'date-time')
                  : '',
                delay: 100,
                placement: 'top',
                container: 'body',
              }"
              v-model="data.unsubscribed"
            />
          </template>
        </HMPaginatedTable>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { mapGetters } from 'vuex';
import { errorToMessage } from '@/core/utils/errors';
import DirtyMixin from '../../../mixins/DirtyMixin';
import { HMDateSelect } from '../../../components/common';
import moment from 'moment/moment';
import EmailPreviewModal from '../../../components/marketing/EmailPreviewModal';
import HMVueEditor from '../../../components/common/HMVueEditor';
import _ from 'lodash';
import HMDialog from '../../../components/common/HMDialog';

export default {
  name: 'EmailDetail',
  components: {
    TopNav,
    NavItem,
    HMVueEditor,
    HMDateSelect,
  },
  mixins: [DirtyMixin],
  computed: {
    ...mapGetters({
      user: 'user/user',
      products: 'product/allTeacherProducts',
      smtp: 'setting/smtp',
      googleMails: 'setting/googleMails',
    }),
    isMailServerConnected() {
      return this.googleMails?.length > 0 || this.isSmtpConnected;
    },
    isSmtpConnected() {
      return this.smtp?.is_smtp_connected;
    },
    query() {
      const query = {
        studioEmailId: this.email?.id,
        q: this.search,
      };
      if (this.opened) query.opened = true;
      if (this.clicked) query.clicked = true;
      if (this.unsubscribed) query.unsubscribed = true;
      return query;
    },
    watchedEmail() {
      return { ...this.email };
    },
    isSent() {
      return this.email.status === 'sent';
    },
    minimumDate() {
      return moment().subtract(1, 'day').toDate();
    },
    selectedAudience() {
      return this.email?.audience?.selected;
    },
    totalAudience() {
      return this.email?.audience?.total;
    },
    purchasedBefore: {
      get() {
        if (!this.email.purchased_before) return null;
        return {
          startDate: this.email.purchased_before,
          endDate: this.email.purchased_before,
        };
      },
      set(value) {
        if (!value) {
          this.email.purchased_before = null;
        } else {
          const date = moment(value.startDate).startOf('day').toDate();
          this.email.purchased_before = date;
        }
      },
    },
    purchasedAfter: {
      get() {
        if (!this.email.purchased_after) return null;
        return {
          startDate: this.email.purchased_after,
          endDate: this.email.purchased_after,
        };
      },
      set(value) {
        if (!value) {
          this.email.purchased_after = null;
        } else {
          const date = moment(value.startDate).endOf('day').toDate();
          this.email.purchased_after = date;
        }
      },
    },
    scheduledDate: {
      get() {
        if (!this.email?.scheduled) return null;
        return {
          startDate: this.email.scheduled,
          endDate: this.email.scheduled,
        };
      },
      set(value) {
        if (!value) {
          this.email.scheduled = null;
        } else {
          this.email.scheduled = value.startDate;
        }
      },
    },
    recipientsTableFields() {
      return [
        {
          name: 'email',
          title: this.$t('MARKETING.TABLE.EMAIL'),
          cssClass: 'h5',
        },
        {
          name: 'sent',
          title: this.$t('MARKETING.TABLE.SENT'),
        },
        {
          name: 'opened',
          title: this.$t('MARKETING.TABLE.OPENED'),
        },
        {
          name: 'clicked',
          title: this.$t('MARKETING.TABLE.CLICKED'),
        },
        {
          name: 'unsubscribed',
          title: this.$t('MARKETING.TABLE.UNSUBSCRIBED'),
        },
      ];
    },
    recipients: {
      get() {
        if (this.email.email_www_customers) return 'www_customers';
        if (this.email.email_everyone) return 'everyone';
        if (!this.email.email_everyone) return 'customers_only';
        return null;
      },
      set(value) {
        this.email.email_everyone = false;
        this.email.email_www_customers = false;
        if (value === 'everyone') this.email.email_everyone = true;
        if (value === 'www_customers') this.email.email_www_customers = true;
      },
    },
    showPurchasedFilters() {
      return !this.email.email_everyone && !this.email.email_www_customers;
    },
    showWWWCustomersFilter() {
      return this.email.email_www_customers;
    },
  },
  data() {
    return {
      isLoading: true,
      isSending: false,
      isPreviewing: false,
      email: null,
      dateBefore: true,
      search: '',
      opened: false,
      clicked: false,
      unsubscribed: false,
    };
  },
  methods: {
    async loadEmail() {
      try {
        this.isLoading = true;
        this.email = await this.$store.dispatch(
          'marketing/getEmail',
          this.$route.params.id
        );
        if (this.email?.purchased_after) {
          this.dateBefore = false;
        }
        this.fetchAudience();
        this.clearDirty();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    async fetchAudience() {
      try {
        const audience = await this.$store.dispatch(
          'marketing/getEmailAudience',
          this.email
        );
        Vue.set(this.email, 'audience', audience);
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    async preview() {
      try {
        if (this.isDirty) await this.save(true);
        this.isPreviewing = true;
        const emailPreview = await this.$store.dispatch(
          'marketing/getEmailPreview',
          this.email.id
        );
        this.$modal.show(
          EmailPreviewModal,
          {
            content: emailPreview.preview,
          },
          {
            classes: ['vm--inline-modal'],
          }
        );
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to load email preview',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isPreviewing = false;
      }
    },
    async save(hideNotify) {
      try {
        this.isLoading = true;
        await this.$store.dispatch('marketing/updateEmail', this.email);
        !hideNotify &&
          this.$notify({
            group: 'app',
            type: 'success',
            title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
            text: this.$t('MARKETING.NOTIFICATIONS.EMAIL_SUCCESSFULLY_SAVED'),
          });
        this.clearDirty();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to save email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isLoading = false;
      }
    },
    async showTestSendModal() {
      if (this.isDirty) await this.save(true);
      this.$modal.show(HMDialog, {
        title: this.$t('GENERAL.TITLE.SEND_EMAIL'),
        text: this.$t('MARKETING.TEXT.CONFIRM_SEND_TEST_EMAIL', {
          email_subject: this.email.subject,
          email_recipient: this.user.email,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.SEND_EMAIL'),
        confirm: async () => {
          await this.testSend();
        },
      });
    },
    async showSendModal() {
      if (!this.selectedAudience) {
        this.$modal.show(HMDialog, {
          title: this.$t('GENERAL.TITLE.SEND_EMAIL'),
          text: this.$t('MARKETING.TEXT.SEND_EMAIL_NO_AUDIENCE', {
            email_subject: this.email.subject,
          }),
          confirmButton: null,
          confirm: async () => {},
        });
        return;
      }
      if (this.isDirty) await this.save(true);
      this.$modal.show(HMDialog, {
        title: this.$t('GENERAL.TITLE.SEND_EMAIL'),
        text: this.$t('MARKETING.TEXT.CONFIRM_SEND_EMAIL', {
          email_subject: this.email.subject,
          recipient_count: this.selectedAudience,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.SEND_EMAIL'),
        confirm: async () => {
          await this.send();
        },
      });
    },
    async testSend() {
      try {
        this.isSending = true;
        await this.save(true);
        await this.$store.dispatch('marketing/sendTestEmail', this.email.id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('MARKETING.NOTIFICATIONS.EMAIL_SUCCESSFULLY_SENT_TO', {
            email: this.user.email,
          }),
        });
        this.clearDirty();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to send email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isSending = false;
      }
    },
    async send() {
      try {
        this.isSending = true;
        await this.save(true);
        await this.$store.dispatch('marketing/sendEmail', this.email.id);
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t('NOTIFICATIONS.TITLE.SUCCESS'),
          text: this.$t('MARKETING.NOTIFICATIONS.EMAIL_SUCCESSFULLY_SENT'),
        });
        this.clearDirty();
        this.$router.replace({
          name: 'marketing-emails',
          query: {
            tab: 'sent',
          },
        });
      } catch (error) {
        console.log(error);
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to send email',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isSending = false;
      }
    },
  },
  async mounted() {
    this.$store.dispatch('product/getTeacherProducts');
    await this.loadEmail();
    this.$store.dispatch('setting/getGoogleMailConnections');
    this.$store.dispatch('setting/getSmtpInfo');
    setTimeout(() => {
      // invalidate initial vue2-editor triggering of the dirty flag
      this.clearDirty();
    }, 300);
  },
  watch: {
    watchedEmail: {
      // we're watching a copy of the email, because deep watch handler sends same new and old objects
      handler: function (email, oldEmail) {
        if (oldEmail && !_.isEqual(oldEmail, email)) {
          this.setDirty();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.filter-section {
  padding: 1rem;
  border: 1px solid $light-gray;
  border-radius: $border-radius;
  display: flex;
  flex-direction: column;

  .head {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
}
hr {
  border-color: $light-gray;
  margin: 0 -1rem 1rem -1rem;
}
::v-deep .daterangepicker {
  .applyBtn {
    background-color: var(--brand-color, $coral) !important;
    color: $white;
    border: none;
    outline: none;
  }

  .start-date {
    background-color: var(--brand-color, $coral) !important;
  }
}
.calendar-icon {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: lighten($coral, 30%);
  color: var(--brand-color, $coral);
}
</style>
